import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, ASSET_STATUSES, ASSET_CATEGORIES, PRIORITIES, FAULT_STATUSES, MAINT_TYPES, PAT_RESULTS } from "@/lib/status";
import PhotoUploader from "@/components/PhotoUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, Pencil, Trash2, TriangleAlert, Wrench, Save, QrCode, Zap, FileText, Tag, Plus, Printer } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, value }) => (
  <div className="flex justify-between gap-3 border-b border-border/60 py-2 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-medium text-foreground">{value || "—"}</span>
  </div>
);

export default function AssetDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [edit, setEdit] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [fault, setFault] = useState(null);
  const [maint, setMaint] = useState(null);
  const [pat, setPat] = useState(null);
  const [delPat, setDelPat] = useState(null);

  const load = useCallback(() => { api.getAsset(id).then(setAsset).catch(() => { toast.error("Asset not found"); nav("/assets"); }); }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  if (!asset) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const saveEdit = async () => {
    try { await api.updateAsset(id, edit); toast.success("Saved"); setEdit(null); load(); }
    catch { toast.error("Save failed"); }
  };
  const doDelete = async () => { try { await api.deleteAsset(id); toast.success("Asset deleted"); nav("/assets"); } catch { toast.error("Delete failed"); } };

  const submitFault = async () => {
    if (!fault.title.trim()) { toast.error("Title required"); return; }
    try { await api.createFault({ ...fault, asset_id: id }); toast.success("Fault logged"); setFault(null); load(); }
    catch { toast.error("Failed"); }
  };
  const submitMaint = async () => {
    if (!maint.title.trim()) { toast.error("Title required"); return; }
    try { await api.createMaint({ ...maint, asset_id: id }); toast.success("Maintenance scheduled"); setMaint(null); load(); }
    catch { toast.error("Failed"); }
  };
  const submitPat = async () => {
    if (!pat.date) { toast.error("Test date required"); return; }
    try { await api.addPat(id, pat); toast.success("PAT test recorded"); setPat(null); nav(`/assets/${id}/pat-tag`); }
    catch { toast.error("Failed"); }
  };
  const doDeletePat = async () => {
    try { await api.deletePat(id, delPat.id); toast.success("PAT record deleted"); setDelPat(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  const qrValue = `${window.location.origin}/assets/${id}`;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-8">
      <button onClick={() => nav("/assets")} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground tap-scale hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All assets</button>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Left: QR + actions */}
        <div className="lg:w-72 lg:shrink-0">
          <div className="flex gap-2">
            <Button data-testid="asset-qr-btn" variant="outline" onClick={() => setShowQr(true)} className="flex-1 gap-2 tap-scale"><QrCode className="h-4 w-4" /> QR code</Button>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Button data-testid="asset-report-btn" variant="outline" onClick={() => nav(`/assets/${id}/report`)} className="gap-2 tap-scale"><FileText className="h-4 w-4" /> Report</Button>
            <Button data-testid="asset-tag-btn" variant="outline" onClick={() => nav(`/assets/tags?ids=${id}`)} className="gap-2 tap-scale"><Tag className="h-4 w-4" /> Print tag</Button>
          </div>
        </div>

        {/* Right: details */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs font-bold text-primary">{asset.asset_code}</span>
                <StatusBadge value={asset.status} testid="asset-detail-status" />
                <StatusBadge value={asset.pat_status} testid="asset-detail-pat" />
              </div>
              <h1 className="mt-1 text-2xl font-bold tracking-tight">{asset.name}</h1>
              {asset.source && <p className="mt-0.5 text-xs text-muted-foreground">{asset.source}</p>}
            </div>
            <div className="flex gap-2">
              <Button data-testid="asset-edit-btn" variant="outline" onClick={() => setEdit({ ...asset })} className="gap-2 tap-scale"><Pencil className="h-4 w-4" /> Edit</Button>
              {user?.role === "admin" && <Button data-testid="asset-delete-btn" variant="outline" onClick={() => setDelOpen(true)} className="gap-2 text-destructive tap-scale hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
            <div>
              <Field label="Category" value={asset.category} />
              <Field label="Make" value={asset.make} />
              <Field label="Model" value={asset.model} />
              <Field label="Serial / ID" value={asset.serial} />
            </div>
            <div>
              <Field label="Location" value={asset.location} />
              <Field label="Purchase date" value={asset.purchase_date} />
              <Field label="Warranty expiry" value={asset.warranty_expiry} />
              <Field label="Value" value={asset.value} />
            </div>
          </div>
          {asset.notes && <p className="mt-3 rounded-lg border border-border bg-secondary/20 p-3 text-sm">{asset.notes}</p>}

          {/* Quick actions */}
          <div className="mt-5 flex flex-wrap gap-2">
            <Button data-testid="asset-log-fault-btn" onClick={() => setFault({ title: "", description: "", priority: "Medium", status: "Open", area: asset.location || "" })} variant="outline" className="gap-2 tap-scale"><TriangleAlert className="h-4 w-4 text-destructive" /> Log fault</Button>
            <Button data-testid="asset-schedule-maint-btn" onClick={() => setMaint({ title: "", type: "Service", description: "", scheduled_date: "", performed_by: "", cost: "" })} variant="outline" className="gap-2 tap-scale"><Wrench className="h-4 w-4 text-primary" /> Schedule maintenance</Button>
          </div>

          {/* PAT / electrical test */}
          <div className="mt-6 rounded-xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground"><Zap className="h-4 w-4 text-amber-500" /> Electrical (PAT) tests</h3>
              <div className="flex items-center gap-2">
                <StatusBadge value={asset.pat_status} />
                <Button data-testid="pat-tag-btn" size="sm" variant="outline" disabled={!asset.pat_tests?.length} onClick={() => nav(`/assets/${id}/pat-tag`)} className="gap-1.5 tap-scale"><Printer className="h-4 w-4" /> Tag</Button>
                <Button data-testid="record-pat-btn" size="sm" variant="outline" onClick={() => setPat({ date: new Date().toISOString().slice(0, 10), result: "Pass", tested_by: user?.name || "", next_due: "", notes: "" })} className="gap-1.5 tap-scale"><Plus className="h-4 w-4" /> Record test</Button>
              </div>
            </div>
            {asset.pat_tests?.length ? (
              <table className="w-full border-collapse text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="py-1 pr-2">Date</th><th className="py-1 pr-2">Result</th><th className="py-1 pr-2">By</th><th className="py-1 pr-2">Next due</th><th className="py-1"></th></tr></thead>
                <tbody>
                  {[...asset.pat_tests].reverse().map((t, i) => (
                    <tr key={t.id || i} className="border-b border-border/50">
                      <td className="py-1.5 pr-2 font-mono">{t.date}</td>
                      <td className="py-1.5 pr-2"><StatusBadge value={t.result} /></td>
                      <td className="py-1.5 pr-2">{t.tested_by || "—"}</td>
                      <td className="py-1.5 pr-2 font-mono">{t.next_due || "—"}</td>
                      <td className="py-1.5 text-right">
                        <Button data-testid={`delete-pat-${t.id || i}`} size="sm" variant="ghost" disabled={!t.id} onClick={() => setDelPat(t)} className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive disabled:opacity-30"><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="py-1 text-sm text-muted-foreground">No PAT tests recorded yet.</p>}
          </div>

          {/* Related */}
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground"><TriangleAlert className="h-4 w-4" /> Faults ({asset.faults?.length || 0})</h3>
              {asset.faults?.length ? asset.faults.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-2 border-b border-border/60 py-2 text-sm last:border-0">
                  <span className="truncate">{f.title}</span><StatusBadge value={f.status} />
                </div>
              )) : <p className="py-2 text-sm text-muted-foreground">None logged.</p>}
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground"><Wrench className="h-4 w-4" /> Maintenance ({asset.maintenance?.length || 0})</h3>
              {asset.maintenance?.length ? asset.maintenance.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-2 border-b border-border/60 py-2 text-sm last:border-0">
                  <span className="truncate">{m.title}<span className="ml-1 text-xs text-muted-foreground">{m.scheduled_date}</span></span><StatusBadge value={m.derived_status} />
                </div>
              )) : <p className="py-2 text-sm text-muted-foreground">None scheduled.</p>}
            </div>
          </div>
        </div>
      </div>

      {/* QR dialog */}
      <Dialog open={showQr} onOpenChange={setShowQr}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-mono">{asset.asset_code}</DialogTitle><DialogDescription>Scan to open this asset.</DialogDescription></DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="rounded-xl bg-white p-4"><QRCodeSVG value={qrValue} size={200} /></div>
            <p className="text-center text-sm font-medium">{asset.name}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>Edit asset</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Name</Label><Input data-testid="edit-asset-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="bg-secondary/40" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Category</Label>
                  <Select value={edit.category || undefined} onValueChange={(v) => setEdit({ ...edit, category: v })}><SelectTrigger className="bg-secondary/40"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{ASSET_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Status</Label>
                  <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}><SelectTrigger data-testid="edit-asset-status" className="bg-secondary/40"><SelectValue /></SelectTrigger><SelectContent>{ASSET_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Make</Label><Input value={edit.make} onChange={(e) => setEdit({ ...edit, make: e.target.value })} className="bg-secondary/40" /></div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Model</Label><Input value={edit.model} onChange={(e) => setEdit({ ...edit, model: e.target.value })} className="bg-secondary/40" /></div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Serial / ID</Label><Input value={edit.serial} onChange={(e) => setEdit({ ...edit, serial: e.target.value })} className="bg-secondary/40" /></div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Location</Label><Input value={edit.location} onChange={(e) => setEdit({ ...edit, location: e.target.value })} className="bg-secondary/40" /></div>
              </div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Notes</Label><Textarea value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className="bg-secondary/40" /></div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Photos</Label><PhotoUploader photos={edit.photos || []} onChange={(p) => setEdit({ ...edit, photos: p })} /></div>
              <Button data-testid="save-asset-btn" onClick={saveEdit} className="w-full gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90"><Save className="h-4 w-4" /> Save changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Log fault dialog */}
      <Dialog open={!!fault} onOpenChange={(o) => !o && setFault(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Log fault · {asset.asset_code}</DialogTitle></DialogHeader>
          {fault && (
            <div className="space-y-4">
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Title</Label><Input data-testid="quick-fault-title" value={fault.title} onChange={(e) => setFault({ ...fault, title: e.target.value })} className="bg-secondary/40" /></div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Description</Label><Textarea value={fault.description} onChange={(e) => setFault({ ...fault, description: e.target.value })} className="bg-secondary/40" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Priority</Label><Select value={fault.priority} onValueChange={(v) => setFault({ ...fault, priority: v })}><SelectTrigger className="bg-secondary/40"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Status</Label><Select value={fault.status} onValueChange={(v) => setFault({ ...fault, status: v })}><SelectTrigger className="bg-secondary/40"><SelectValue /></SelectTrigger><SelectContent>{FAULT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <Button data-testid="quick-fault-submit" onClick={submitFault} className="w-full bg-primary text-primary-foreground tap-scale hover:bg-primary/90">Log fault</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule maintenance dialog */}
      <Dialog open={!!maint} onOpenChange={(o) => !o && setMaint(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Schedule maintenance · {asset.asset_code}</DialogTitle></DialogHeader>
          {maint && (
            <div className="space-y-4">
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Title</Label><Input data-testid="quick-maint-title" value={maint.title} onChange={(e) => setMaint({ ...maint, title: e.target.value })} className="bg-secondary/40" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Type</Label><Select value={maint.type} onValueChange={(v) => setMaint({ ...maint, type: v })}><SelectTrigger className="bg-secondary/40"><SelectValue /></SelectTrigger><SelectContent>{MAINT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Scheduled date</Label><Input type="date" value={maint.scheduled_date} onChange={(e) => setMaint({ ...maint, scheduled_date: e.target.value })} className="bg-secondary/40" /></div>
              </div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Description</Label><Textarea value={maint.description} onChange={(e) => setMaint({ ...maint, description: e.target.value })} className="bg-secondary/40" /></div>
              <Button data-testid="quick-maint-submit" onClick={submitMaint} className="w-full bg-primary text-primary-foreground tap-scale hover:bg-primary/90">Schedule</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record PAT dialog */}
      <Dialog open={!!pat} onOpenChange={(o) => !o && setPat(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record PAT test · {asset.asset_code}</DialogTitle><DialogDescription>Log an electrical (portable appliance) test result.</DialogDescription></DialogHeader>
          {pat && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Date tested</Label><Input data-testid="pat-date" type="date" value={pat.date} onChange={(e) => setPat({ ...pat, date: e.target.value })} className="bg-secondary/40" /></div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Result</Label><Select value={pat.result} onValueChange={(v) => setPat({ ...pat, result: v })}><SelectTrigger data-testid="pat-result" className="bg-secondary/40"><SelectValue /></SelectTrigger><SelectContent>{PAT_RESULTS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Tested by</Label><Input value={pat.tested_by} onChange={(e) => setPat({ ...pat, tested_by: e.target.value })} className="bg-secondary/40" /></div>
                <div><Label className="mb-1.5 block text-xs text-muted-foreground">Next due</Label><Input data-testid="pat-next-due" type="date" value={pat.next_due} onChange={(e) => setPat({ ...pat, next_due: e.target.value })} className="bg-secondary/40" /></div>
              </div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Notes</Label><Textarea value={pat.notes} onChange={(e) => setPat({ ...pat, notes: e.target.value })} className="bg-secondary/40" /></div>
              <Button data-testid="pat-submit" onClick={submitPat} className="w-full bg-primary text-primary-foreground tap-scale hover:bg-primary/90">Save test</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this asset?</AlertDialogTitle><AlertDialogDescription>“{asset.name}” will be removed from the inventory.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-asset" onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delPat} onOpenChange={(o) => !o && setDelPat(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this PAT record?</AlertDialogTitle><AlertDialogDescription>The test dated {delPat?.date} will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-pat" onClick={doDeletePat} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
